import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import PageWrapper from "components/PageWrapper";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystatePrColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import { HUMANIZE_PROVIDER } from "constants/vcs_providers";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Stack, VcsProvider } from "types/generated";

import { StackContext } from "../Context";
import StackDetachedIntegrationCallout from "../components/DetachedIntegrationCallout";
import StackHeader from "../components/Header";
import { getStacksBackUrl } from "../helpers";
import PullRequestElement from "./PullRequestElement";
import { LIST_PRS } from "./gql";

const StackPullRequests = () => {
  const { stack: stackCtx } = useTypedContext(StackContext);

  const providerBasedName =
    stackCtx.provider === VcsProvider.Gitlab ? "Merge Request" : "Pull Request";

  useTitle(`${providerBasedName}s · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { loading, error, data, refetch } = useQuery<{ stack: Stack }>(LIST_PRS, {
    variables: { stackId: stackCtx.id },
    skip: stackCtx.vcsDetached,
  });

  const stack = useMemo(
    () => ({
      ...stackCtx,
      ...(data?.stack ? data.stack : {}),
    }),
    [data?.stack, stackCtx]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  // Do not show 'loading' when polling in the background.
  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  // beware the `vcs Detached` is querying only for the stack context
  if (!data?.stack && !stackCtx.vcsDetached) {
    return <NotFoundPage />;
  }

  const pageTitle = stackCtx.provider === VcsProvider.Gitlab ? "Merge requests" : "Pull requests";
  const emptyStateTitle =
    stackCtx.provider === VcsProvider.Gitlab ? "No merge requests yet" : "No pull requests yet";

  return (
    <>
      <StackHeader />

      <PageInfoHeader title={pageTitle}>
        <Button
          onPress={() => refetch()}
          disabled={loading}
          loading={!!(loading && data?.stack)}
          variant="primary"
        >
          Refresh
        </Button>
      </PageInfoHeader>

      <StackDetachedIntegrationCallout />

      <PageWrapper>
        {!stackCtx.vcsDetached &&
          stack.pullRequests?.map((pr) => (
            <PullRequestElement key={pr.id} repository={stack.repository} pr={pr} />
          ))}

        {stackCtx.vcsDetached && (
          <EmptyState
            title="Source code integration was detached from this stack."
            icon={EmptystatePrColored}
          />
        )}

        {!stackCtx.vcsDetached && stack.pullRequests?.length === 0 && (
          <EmptyState
            title={emptyStateTitle}
            icon={EmptystatePrColored}
            caption={
              <>
                This is a list of {providerBasedName}s currently open against this stack's tracked
                branch on {HUMANIZE_PROVIDER[stack.provider]}. If something seems missing, it may be
                because the {providerBasedName} was opened before the creation of this stack, or it
                may be targeting a different branch.
              </>
            }
          />
        )}
      </PageWrapper>
    </>
  );
};

export default StackPullRequests;
